<script setup>
  import { computed, onMounted, ref } from "vue";
  import { useStore } from "vuex";

  import {
    Globe,
    Power,
    Loader2,
    ExternalLink,
    TriangleAlert,
    CheckCircle2,
    X,
    Copy,
    Lock,
    ShieldCheck,
    RefreshCw,
    Network,
    Sparkles,
  } from "lucide-vue-next";

  import { useGlobalToast } from "@/utils/useGlobalToast";

  const { showToast } = useGlobalToast();
  const store = useStore();

  /* =========================================
  STATE
  ========================================= */
  const domain = ref("");
  const loading = ref(false);
  const confirmModal = ref(false);
  const actionType = ref("");
  const sslActivated = ref(false);
  const SERVER_IP = "206.189.129.218";

  // DNS Guide Tab State ('a' or 'cname')
  const recordType = ref("a");

  // Interactive UI Simulation State
  const checkingDns = ref(false);
  const dnsVerified = ref(false);

  /* =========================================
  STORE
  ========================================= */
  const website = computed(() => store.getters.getWebsiteDetails);

  /* =========================================
  FETCH
  ========================================= */
  onMounted(async () => {
    try {
      await store.dispatch("fetchWebsiteDetails");

      if (website.value?.customDomain) {
        domain.value = website.value.customDomain;
        sslActivated.value = true;
        dnsVerified.value = true;
      }
    } catch (err) {
      console.error(err);
    }
  });

  /* =========================================
  COPY HELPERS
  ========================================= */
  const copyIP = async () => {
    try {
      await navigator.clipboard.writeText(SERVER_IP);
      showToast("success", "Server IP copied to clipboard!");
    } catch (err) {
      console.error(err);
    }
  };

  const copyDefaultUrl = async () => {
    try {
      const url = `${website.value?.slug || "instance"}.educanium.com`;
      await navigator.clipboard.writeText(url);
      showToast("success", "Target URL copied to clipboard!");
    } catch (err) {
      console.error(err);
    }
  };

  /* =========================================
  SIMULATE DNS CHECK
  ========================================= */
  const checkDNSConfig = () => {
    if (!domain.value) return;
    checkingDns.value = true;
    setTimeout(() => {
      checkingDns.value = false;
      dnsVerified.value = true;
      showToast("success", "DNS records found! You are ready to connect.");
    }, 1200);
  };

  /* =========================================
  MODAL CONTROLS
  ========================================= */
  const openConfirmation = (type) => {
    actionType.value = type;
    confirmModal.value = true;
  };

  const closeModal = () => {
    confirmModal.value = false;
  };

  /* =========================================
  SAVE DOMAIN + AUTO SSL
  ========================================= */
  const saveDomain = async () => {
    try {
      loading.value = true;
      await store.dispatch("connectCustomDomain", domain.value);

      showToast(
        "success",
        website.value?.customDomain
          ? "Domain updated successfully."
          : "Domain connected successfully.",
      );

      try {
        const sslRes = await store.dispatch("canIssueCert", domain.value);
        sslActivated.value = true;
        showToast(
          "success",
          sslRes.message ||
            "Security certificate (SSL) enabled! HTTPS is live.",
        );
      } catch (sslErr) {
        showToast(
          "error",
          sslErr.response?.data?.message ||
            "Domain saved, but security certificate setup failed.",
        );
      }

      await store.dispatch("fetchWebsiteDetails");
      closeModal();
    } catch (err) {
      showToast(
        "error",
        err.response?.data?.message || "Failed to connect domain",
      );
    } finally {
      loading.value = false;
    }
  };

  /* =========================================
  WEBSITE TOGGLE
  ========================================= */
  const toggleWebsite = async () => {
    try {
      loading.value = true;
      await store.dispatch("toggleWebsiteStatus");
      await store.dispatch("fetchWebsiteDetails");

      showToast(
        "success",
        website.value?.websiteStatus === "active"
          ? "Website is now live online."
          : "Website turned off successfully.",
      );

      closeModal();
    } catch (err) {
      showToast(
        "error",
        err.response?.data?.message || "Failed to update website status",
      );
    } finally {
      loading.value = false;
    }
  };

  /* =========================================
  CONFIRM ACTION
  ========================================= */
  const confirmAction = async () => {
    if (actionType.value === "domain") {
      await saveDomain();
    }
    if (actionType.value === "website") {
      await toggleWebsite();
    }
  };
</script>

<template>
  <div
    class="w-full text-slate-900 font-sans antialiased pb-16 px-3 sm:px-6 max-w-7xl mx-auto space-y-8"
  >
    <!-- TOP PAGE HEADER BANNER -->
    <div
      class="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 sm:p-8 rounded-3xl border border-slate-200/80 shadow-xs"
    >
      <div>
        <div
          class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-extrabold tracking-wider uppercase mb-3 border border-emerald-200/60"
        >
          <Sparkles :size="13" />
          <span>Educanium Network</span>
        </div>
        <h1
          class="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900"
        >
          Domain & Hosting Manager
        </h1>
        <p class="text-xs sm:text-sm text-slate-500 mt-1 max-w-xl">
          Connect your custom branded domain (<strong class="text-slate-800"
            >yourschool.com</strong
          >) and control public availability.
        </p>
      </div>

      <!-- Live Quick Status Badge -->
      <div
        class="flex items-center gap-3 bg-slate-50 border border-slate-200/80 p-3.5 rounded-2xl shrink-0"
      >
        <div class="relative flex h-3 w-3">
          <span
            class="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
            :class="
              website?.websiteStatus === 'active'
                ? 'bg-emerald-400'
                : 'bg-rose-400'
            "
          ></span>
          <span
            class="relative inline-flex rounded-full h-3 w-3"
            :class="
              website?.websiteStatus === 'active'
                ? 'bg-emerald-500'
                : 'bg-rose-500'
            "
          ></span>
        </div>
        <div>
          <span
            class="text-[10px] font-bold uppercase tracking-wider text-slate-400 block"
            >Status</span
          >
          <span class="text-xs font-bold capitalize text-slate-800">{{
            website?.websiteStatus === "active"
              ? "Online & Public"
              : "Offline / Hidden"
          }}</span>
        </div>
      </div>
    </div>

    <!-- GRID CONTAINER -->
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
      <!-- LEFT COLUMN: DOMAIN SETTINGS (8 Cols) -->
      <section
        class="lg:col-span-8 bg-white border border-slate-200/80 rounded-3xl shadow-xs overflow-hidden p-6 sm:p-8 space-y-6"
      >
        <div
          class="flex items-center justify-between border-b border-slate-100 pb-4"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-2xl bg-[#0f2423] text-[#d4af37] flex items-center justify-center shadow-md"
            >
              <Globe :size="20" />
            </div>
            <div>
              <h2
                class="text-base font-extrabold text-slate-900 tracking-tight"
              >
                Custom Domain Setup
              </h2>
              <p class="text-xs text-slate-500">
                Configure DNS records to link your professional URL
              </p>
            </div>
          </div>
        </div>

        <!-- DOMAIN STATUS CARDS -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <!-- Free Default Link -->
          <div
            class="bg-slate-50 border border-slate-200/70 rounded-2xl p-4 flex flex-col justify-between"
          >
            <span
              class="text-[10px] font-bold uppercase tracking-wider text-slate-400"
              >Free System Subdomain</span
            >
            <div class="flex items-center justify-between gap-2 mt-2">
              <span
                class="text-xs sm:text-sm font-bold text-slate-800 break-all"
              >
                {{ website?.slug || "instance" }}.educanium.com
              </span>
              <a
                :href="`https://${website?.slug || 'instance'}.educanium.com`"
                target="_blank"
                class="w-8 h-8 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-[#0f2423] hover:text-[#d4af37] transition shadow-xs shrink-0"
                title="Open free link"
              >
                <ExternalLink :size="14" />
              </a>
            </div>
          </div>

          <!-- Custom Domain Card -->
          <div
            class="border rounded-2xl p-4 flex flex-col justify-between transition"
            :class="
              website?.customDomain
                ? 'bg-emerald-50/40 border-emerald-200'
                : 'bg-slate-50 border-slate-200/70'
            "
          >
            <div class="flex items-center justify-between">
              <span
                class="text-[10px] font-bold uppercase tracking-wider text-slate-400"
                >Custom Domain</span
              >
              <span
                v-if="website?.customDomain"
                class="text-[10px] font-extrabold px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800"
              >
                Connected
              </span>
              <span
                v-else
                class="text-[10px] font-extrabold px-2.5 py-0.5 rounded-full bg-slate-200 text-slate-600"
              >
                Not Linked
              </span>
            </div>
            <div class="mt-2">
              <span
                class="text-xs sm:text-sm font-bold break-all"
                :class="
                  website?.customDomain ? 'text-emerald-950' : 'text-slate-400'
                "
              >
                {{ website?.customDomain || "No domain added yet" }}
              </span>
            </div>
          </div>
        </div>

        <!-- INTERACTIVE DNS GUIDE -->
        <div
          class="bg-slate-50 border border-slate-200/80 rounded-2xl p-5 space-y-4"
        >
          <div
            class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-200/60"
          >
            <div class="flex items-center gap-2">
              <Network
                :size="16"
                class="text-indigo-600"
              />
              <span
                class="text-xs font-bold text-slate-800 uppercase tracking-wider"
                >DNS Record Configuration Guide</span
              >
            </div>

            <!-- Switcher Tabs -->
            <div
              class="grid grid-cols-2 sm:flex bg-slate-200/70 p-1 rounded-xl"
            >
              <button
                @click="recordType = 'a'"
                type="button"
                class="px-3 py-1.5 text-center text-xs font-bold rounded-lg transition"
                :class="
                  recordType === 'a'
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                "
              >
                Root Domain (A)
              </button>
              <button
                @click="recordType = 'cname'"
                type="button"
                class="px-3 py-1.5 text-center text-xs font-bold rounded-lg transition"
                :class="
                  recordType === 'cname'
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                "
              >
                Subdomain (CNAME)
              </button>
            </div>
          </div>

          <!-- A Record Content -->
          <div
            v-if="recordType === 'a'"
            class="space-y-3 text-xs text-slate-700"
          >
            <p class="leading-relaxed">
              1. Log in to your domain registrar (GoDaddy, Hostinger,
              Cloudflare) and open your <strong>DNS Settings</strong>.
            </p>
            <p class="leading-relaxed">
              2. Add an <strong>A Record</strong> matching the details below:
            </p>
            <div
              class="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs"
            >
              <div
                class="grid grid-cols-12 px-3.5 py-2 bg-slate-100 font-bold text-[10px] text-slate-600 uppercase tracking-wider border-b border-slate-200"
              >
                <span class="col-span-3">Type</span>
                <span class="col-span-3">Host</span>
                <span class="col-span-6">Points To</span>
              </div>
              <div
                class="grid grid-cols-12 px-3.5 py-2.5 items-center font-mono text-xs text-slate-800"
              >
                <span class="col-span-3 text-emerald-600 font-bold">A</span>
                <span class="col-span-3">@</span>
                <div class="col-span-6 flex items-center justify-between gap-1">
                  <span class="font-bold text-slate-900 truncate">{{
                    SERVER_IP
                  }}</span>
                  <button
                    @click="copyIP"
                    class="text-slate-400 hover:text-indigo-600 p-1 transition"
                    title="Copy IP"
                  >
                    <Copy :size="13" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- CNAME Record Content -->
          <div
            v-else
            class="space-y-3 text-xs text-slate-700"
          >
            <p class="leading-relaxed">
              1. Open your provider's <strong>DNS Settings</strong> panel.
            </p>
            <p class="leading-relaxed">
              2. Add a <strong>CNAME Record</strong> for your subdomain (e.g.,
              <code>www.yourschool.com</code>):
            </p>
            <div
              class="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs"
            >
              <div
                class="grid grid-cols-12 px-3.5 py-2 bg-slate-100 font-bold text-[10px] text-slate-600 uppercase tracking-wider border-b border-slate-200"
              >
                <span class="col-span-3">Type</span>
                <span class="col-span-3">Host</span>
                <span class="col-span-6">Points To</span>
              </div>
              <div
                class="grid grid-cols-12 px-3.5 py-2.5 items-center font-mono text-xs text-slate-800"
              >
                <span class="col-span-3 text-purple-600 font-bold">CNAME</span>
                <span class="col-span-3">www</span>
                <div class="col-span-6 flex items-center justify-between gap-1">
                  <span class="font-bold text-slate-900 truncate"
                    >{{ website?.slug || "instance" }}.educanium.com</span
                  >
                  <button
                    @click="copyDefaultUrl"
                    class="text-slate-400 hover:text-indigo-600 p-1 transition"
                    title="Copy Target Link"
                  >
                    <Copy :size="13" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- INPUT & ACTIONS -->
        <div class="space-y-3 pt-2">
          <label
            class="block text-xs font-bold uppercase tracking-wider text-slate-700"
          >
            Enter Your Custom Domain
          </label>
          <div class="flex flex-col sm:flex-row gap-3">
            <div class="relative flex items-center flex-1">
              <Globe
                class="absolute left-3.5 text-slate-400 pointer-events-none"
                :size="16"
              />
              <input
                v-model="domain"
                type="text"
                placeholder="e.g. myschool.com or www.myschool.com"
                class="w-full h-12 pl-10 pr-4 rounded-xl border border-slate-200 bg-slate-50/50 text-xs sm:text-sm font-semibold text-slate-800 outline-none transition focus:bg-white focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/10"
                @input="dnsVerified = false"
              />
            </div>

            <div class="flex gap-2">
              <button
                v-if="domain && !dnsVerified"
                @click="checkDNSConfig"
                :disabled="checkingDns"
                class="h-12 px-4 rounded-xl text-xs font-bold whitespace-nowrap inline-flex items-center gap-1.5 transition border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 active:scale-95 disabled:opacity-50 cursor-pointer"
              >
                <RefreshCw
                  v-if="checkingDns"
                  class="animate-spin"
                  :size="14"
                />
                <Network
                  v-else
                  :size="14"
                />
                Test DNS
              </button>

              <button
                @click="openConfirmation('domain')"
                :disabled="loading || !domain"
                class="h-12 px-6 rounded-xl text-xs font-bold whitespace-nowrap inline-flex items-center gap-2 transition bg-emerald-600 text-white shadow-md shadow-emerald-600/20 hover:bg-emerald-500 active:scale-95 disabled:opacity-50 cursor-pointer flex-1 sm:flex-none justify-center"
              >
                <Loader2
                  v-if="loading"
                  class="animate-spin"
                  :size="14"
                />
                <ShieldCheck
                  v-else
                  :size="14"
                />
                {{ website?.customDomain ? "Update Domain" : "Save Domain" }}
              </button>
            </div>
          </div>
        </div>

        <!-- SSL NOTIFICATION -->
        <div
          v-if="sslActivated"
          class="flex gap-3 p-4 rounded-2xl items-start bg-emerald-50 border border-emerald-200/80"
        >
          <div
            class="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 bg-emerald-600 text-white shadow-xs"
          >
            <Lock :size="15" />
          </div>
          <div>
            <h4 class="text-xs font-extrabold text-emerald-950">
              SSL Certificate Protected (HTTPS Active)
            </h4>
            <p class="text-xs text-emerald-700 mt-0.5 leading-relaxed">
              Your domain is securely encrypted with an automatic SSL
              certificate.
            </p>
          </div>
        </div>
      </section>

      <!-- RIGHT COLUMN: WEBSITE ACCESS TOGGLE (4 Cols) -->
      <section
        class="lg:col-span-4 bg-white border border-slate-200/80 rounded-3xl shadow-xs overflow-hidden p-6 sm:p-8 space-y-6"
      >
        <div class="border-b border-slate-100 pb-4">
          <h2 class="text-base font-extrabold text-slate-900 tracking-tight">
            Public Availability
          </h2>
          <p class="text-xs text-slate-500">Manage global website visibility</p>
        </div>

        <div class="space-y-4">
          <div
            class="p-4 rounded-2xl bg-slate-50 border border-slate-200/70 space-y-2"
          >
            <span class="text-xs font-bold text-slate-800 block"
              >Current Visibility</span
            >
            <p class="text-xs text-slate-500 leading-relaxed">
              {{
                website?.websiteStatus === "active"
                  ? "Your school portal is currently live and accessible to the public internet."
                  : "Your portal is offline. Visitors will see a maintenance screen."
              }}
            </p>
          </div>

          <button
            @click="openConfirmation('website')"
            class="w-full h-12 px-5 rounded-xl text-xs font-bold inline-flex items-center justify-center gap-2 transition shadow-md active:scale-95 cursor-pointer"
            :class="
              website?.websiteStatus === 'active'
                ? 'bg-rose-600 text-white hover:bg-rose-500 shadow-rose-600/25'
                : 'bg-emerald-600 text-white hover:bg-emerald-500 shadow-emerald-600/25'
            "
          >
            <Power :size="16" />
            {{
              website?.websiteStatus === "active"
                ? "Turn Off Website"
                : "Turn On Website"
            }}
          </button>
        </div>
      </section>
    </div>

    <!-- CONFIRMATION MODAL -->
    <transition name="modal-fade">
      <div
        v-if="confirmModal"
        class="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-[1000] flex items-center justify-center p-4"
        @click.self="closeModal"
      >
        <div
          class="bg-white rounded-3xl w-full max-w-md p-6 relative shadow-2xl transition-all border-t-4"
          :class="
            actionType === 'website' && website?.websiteStatus === 'active'
              ? 'border-rose-600'
              : 'border-emerald-600'
          "
        >
          <button
            @click="closeModal"
            class="absolute top-5 right-5 w-8 h-8 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center hover:bg-slate-200 hover:text-slate-700 transition"
          >
            <X :size="15" />
          </button>

          <div class="flex justify-center mb-4">
            <div
              v-if="
                actionType === 'website' && website?.websiteStatus === 'active'
              "
              class="w-14 h-14 rounded-2xl flex items-center justify-center bg-rose-50 text-rose-600"
            >
              <TriangleAlert :size="26" />
            </div>
            <div
              v-else
              class="w-14 h-14 rounded-2xl flex items-center justify-center bg-emerald-50 text-emerald-600"
            >
              <CheckCircle2 :size="26" />
            </div>
          </div>

          <div class="text-center mb-6 space-y-2">
            <h3 class="text-lg font-bold text-slate-900">Confirm Action</h3>
            <p class="text-xs sm:text-sm text-slate-500 leading-relaxed">
              {{
                actionType === "domain"
                  ? "Are you sure you want to connect this domain? Ensure your DNS settings are configured properly at your registrar."
                  : website?.websiteStatus === "active"
                  ? "Are you sure you want to turn off your website? Visitors will temporarily lose access to your public pages."
                  : "Are you ready to turn your website back online for all users?"
              }}
            </p>
          </div>

          <div class="flex gap-3">
            <button
              @click="closeModal"
              class="flex-1 h-11 rounded-xl text-xs font-bold bg-slate-100 text-slate-700 hover:bg-slate-200 transition cursor-pointer"
            >
              Cancel
            </button>
            <button
              @click="confirmAction"
              class="flex-1 h-11 rounded-xl text-xs font-bold text-white transition active:scale-95 cursor-pointer shadow-md"
              :class="
                actionType === 'website' && website?.websiteStatus === 'active'
                  ? 'bg-rose-600 hover:bg-rose-500 shadow-rose-600/20'
                  : 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-600/20'
              "
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
  .modal-fade-enter-active,
  .modal-fade-leave-active {
    transition: opacity 0.2s ease, transform 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .modal-fade-enter-from,
  .modal-fade-leave-to {
    opacity: 0;
    transform: scale(0.96) translateY(4px);
  }
</style>
